import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { 
  Search, Plus, MapPin, Phone, Mail, Globe, User, FileText, X, Loader2,
  Download, RefreshCw, Factory, Trash2, Filter, Menu, Building2, Users,
  Calendar, MessageSquare, Send, Clock, Briefcase, Bell, Check, AlertCircle,
  LogOut, Shield, MoreVertical, EyeOff, Undo2, Settings, Save, Star, CheckSquare, Square
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Toaster, toast } from "sonner";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios to send cookies
axios.defaults.withCredentials = true;

const STATUS_COLORS = {
  "nem nyert": "#ef4444",
  "visszahívni": "#3b82f6",
  "esélyes": "#14b8a6",
  "időpont megbeszélve": "#f97316",
  "ajánlat elküldve": "#eab308",
  "aktív szerződés": "#22c55e",
  "jelenleg passzív partner": "#6b7280",
};

// Quick log buttons - removed Telefonhívás
const QUICK_LOG_BUTTONS = [
  { type: "Hideg email elküldve", icon: Mail, color: "bg-blue-100 text-blue-700 hover:bg-blue-200" },
  { type: "Bemutatkozó elküldve", icon: Send, color: "bg-purple-100 text-purple-700 hover:bg-purple-200" },
  { type: "Árajánlat elküldve", icon: FileText, color: "bg-green-100 text-green-700 hover:bg-green-200" },
  { type: "Meeting", icon: Users, color: "bg-pink-100 text-pink-700 hover:bg-pink-200" },
  { type: "Follow-up", icon: RefreshCw, color: "bg-yellow-100 text-yellow-700 hover:bg-yellow-200" },
];

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('hu-HU', { year: 'numeric', month: '2-digit', day: '2-digit' });
};

const formatDateTime = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('hu-HU', { 
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit'
  });
};

// Login Screen
function LoginScreen({ onLogin }) {
  const [identifier, setIdentifier] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!identifier.trim()) return;
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/auth/login`, { identifier: identifier.trim() });
      onLogin(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Hiba a bejelentkezéskor');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4" data-testid="login-screen">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4">
            <Building2 className="w-8 h-8 text-blue-500" />
            <h1 className="text-2xl font-bold text-white">Optimal CRM</h1>
          </div>
          <p className="text-gray-400 text-sm">Partner Nyilvántartó Rendszer</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-gray-800 border border-gray-700 p-6 space-y-4">
          <div>
            <Label className="text-gray-300 text-xs uppercase tracking-wider">Azonosító</Label>
            <Input
              data-testid="login-identifier-input"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder="pl. pecsibrig545"
              className="bg-gray-900 border-gray-600 text-white placeholder:text-gray-500 mt-1"
              autoFocus
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 text-red-400 text-sm" data-testid="login-error">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={loading || !identifier.trim()}
            className="w-full"
            data-testid="login-submit-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Bejelentkezés
          </Button>
        </form>

      </div>
    </div>
  );
}

// Reminder Dialog - pops up after logging
function ReminderDialog({ open, onOpenChange, companyId, companyName, logType, onCreated }) {
  const [remindDate, setRemindDate] = useState('');
  const [remindTime, setRemindTime] = useState('09:00');
  const [nextStep, setNextStep] = useState('');
  const [loading, setLoading] = useState(false);
  const [skipReminder, setSkipReminder] = useState(false);

  // Set default date to tomorrow
  useEffect(() => {
    if (open) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setRemindDate(tomorrow.toISOString().split('T')[0]);
      setNextStep('');
      setSkipReminder(false);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!remindDate || !nextStep) {
      toast.error("Add meg a dátumot és a következő lépést!");
      return;
    }

    setLoading(true);
    try {
      const remindAt = new Date(`${remindDate}T${remindTime}:00`);
      await axios.post(`${API}/companies/${companyId}/reminders`, {
        remind_at: remindAt.toISOString(),
        next_step: nextStep,
        note: logType
      });
      toast.success(`Emlékeztető beállítva: ${formatDateTime(remindAt)}`);
      onOpenChange(false);
      if (onCreated) onCreated();
    } catch (e) {
      toast.error("Hiba az emlékeztető mentésekor");
    }
    setLoading(false);
  };

  const handleSkip = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-orange-500" />
            Emlékeztető beállítása
          </DialogTitle>
          <DialogDescription>
            {companyName} - {logType}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <Label className="text-sm font-medium">Mikor emlékeztessen?</Label>
            <div className="flex gap-2 mt-1">
              <Input 
                type="date" 
                value={remindDate}
                onChange={(e) => setRemindDate(e.target.value)}
                className="flex-1"
              />
              <Input 
                type="time" 
                value={remindTime}
                onChange={(e) => setRemindTime(e.target.value)}
                className="w-28"
              />
            </div>
          </div>

          <div>
            <Label className="text-sm font-medium">Mi legyen a következő lépés?</Label>
            <Textarea
              value={nextStep}
              onChange={(e) => setNextStep(e.target.value)}
              placeholder="pl. Felhívni és egyeztetni a részleteket..."
              rows={3}
              className="mt-1"
            />
          </div>

          {/* Quick suggestions */}
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => setNextStep("Felhívni")}>
              Felhívni
            </Button>
            <Button variant="outline" size="sm" onClick={() => setNextStep("Email küldeni")}>
              Email küldeni
            </Button>
            <Button variant="outline" size="sm" onClick={() => setNextStep("Ajánlatot követni")}>
              Ajánlatot követni
            </Button>
            <Button variant="outline" size="sm" onClick={() => setNextStep("Meeting egyeztetni")}>
              Meeting egyeztetni
            </Button>
          </div>
        </div>

        <DialogFooter className="flex gap-2">
          <Button variant="ghost" onClick={handleSkip}>
            Kihagyom
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Bell className="w-4 h-4 mr-2" /> Emlékeztető beállítása</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Due reminders notification panel
function DueRemindersPanel({ reminders, onMarkDone, onOpenCompany, onSendEmail }) {
  if (reminders.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 w-80 bg-white rounded-lg shadow-lg border border-orange-200 z-50 max-h-96 overflow-hidden flex flex-col">
      <div className="bg-orange-500 text-white px-4 py-2 flex items-center gap-2">
        <Bell className="w-4 h-4" />
        <span className="font-medium">Emlékeztetők ({reminders.length})</span>
      </div>
      <div className="overflow-y-auto flex-1">
        {reminders.map(r => (
          <div key={r.id} className="p-3 border-b hover:bg-orange-50 cursor-pointer" onClick={() => onOpenCompany(r.company_id)}>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="font-medium text-sm">{r.company_name}</p>
                <p className="text-sm text-gray-600">{r.next_step}</p>
                <p className="text-xs text-gray-400 mt-1">
                  <Clock className="w-3 h-3 inline mr-1" />
                  {formatDateTime(r.remind_at)}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={(e) => { e.stopPropagation(); onSendEmail(r.id); }}
                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 h-7 w-7 p-0"
                  title="Email küldése"
                >
                  <Mail className="w-4 h-4" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={(e) => { e.stopPropagation(); onMarkDone(r.id); }}
                  className="text-green-600 hover:text-green-700 hover:bg-green-50 h-7 w-7 p-0"
                  title="Kész"
                >
                  <Check className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Company Detail Sheet with Positions and Logs
function CompanyDetailSheet({ company, open, onOpenChange, onUpdate, onDelete, onFetchData, industries, statuses, onRefresh, userPermissions }) {
  const [activeTab, setActiveTab] = useState("info");
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Positions state
  const [positions, setPositions] = useState([]);
  const [newPosition, setNewPosition] = useState({ name: '', headcount: 1, notes: '' });
  const [addingPosition, setAddingPosition] = useState(false);
  
  // Logs state
  const [logs, setLogs] = useState([]);
  const [customLogContent, setCustomLogContent] = useState('');
  const [addingLog, setAddingLog] = useState(false);

  // Reminder state
  const [showReminderDialog, setShowReminderDialog] = useState(false);
  const [lastLogType, setLastLogType] = useState('');
  const [reminders, setReminders] = useState([]);

  useEffect(() => {
    if (company?.id) {
      setEditData(company);
      setIsEditing(false);
      fetchPositions();
      fetchLogs();
      fetchReminders();
    }
  }, [company?.id]);

  const fetchPositions = async () => {
    if (!company?.id) return;
    try {
      const res = await axios.get(`${API}/companies/${company.id}/positions`);
      setPositions(res.data);
    } catch (e) { console.error(e); }
  };

  const fetchLogs = async () => {
    if (!company?.id) return;
    try {
      const res = await axios.get(`${API}/companies/${company.id}/logs`);
      setLogs(res.data);
    } catch (e) { console.error(e); }
  };

  const fetchReminders = async () => {
    if (!company?.id) return;
    try {
      const res = await axios.get(`${API}/companies/${company.id}/reminders`);
      setReminders(res.data);
    } catch (e) { console.error(e); }
  };

  const handleAddPosition = async () => {
    if (!newPosition.name) {
      toast.error("Add meg a pozíció nevét!");
      return;
    }
    setAddingPosition(true);
    try {
      await axios.post(`${API}/companies/${company.id}/positions`, newPosition);
      setNewPosition({ name: '', headcount: 1, notes: '' });
      await fetchPositions();
      await fetchLogs();
      toast.success("Pozíció hozzáadva!");
    } catch (e) {
      toast.error("Hiba a pozíció hozzáadásakor");
    }
    setAddingPosition(false);
  };

  const handleDeletePosition = async (positionId) => {
    try {
      await axios.delete(`${API}/positions/${positionId}`);
      await fetchPositions();
      toast.success("Pozíció törölve!");
    } catch (e) {
      toast.error("Hiba a törléskor");
    }
  };

  const handleQuickLog = async (logType) => {
    try {
      await axios.post(`${API}/companies/${company.id}/logs/quick?entry_type=${encodeURIComponent(logType)}`);
      await fetchLogs();
      toast.success(logType);
      // Show reminder dialog
      setLastLogType(logType);
      setShowReminderDialog(true);
    } catch (e) {
      toast.error("Hiba a naplózáskor");
    }
  };

  const handleCustomLog = async () => {
    if (!customLogContent.trim()) {
      toast.error("Írj be valamit!");
      return;
    }
    setAddingLog(true);
    try {
      await axios.post(`${API}/companies/${company.id}/logs`, {
        entry_type: "Egyéb",
        content: customLogContent
      });
      const logContent = customLogContent;
      setCustomLogContent('');
      await fetchLogs();
      toast.success("Bejegyzés mentve!");
      // Show reminder dialog
      setLastLogType(logContent);
      setShowReminderDialog(true);
    } catch (e) {
      toast.error("Hiba a mentéskor");
    }
    setAddingLog(false);
  };

  const handleMarkReminderDone = async (reminderId) => {
    try {
      await axios.patch(`${API}/reminders/${reminderId}/done`);
      await fetchReminders();
      await fetchLogs();
      toast.success("Emlékeztető kész!");
    } catch (e) {
      toast.error("Hiba");
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await onUpdate(company.id, editData);
      setIsEditing(false);
      toast.success("Partner frissítve!");
    } catch (e) {
      toast.error("Hiba a mentéskor");
    }
    setLoading(false);
  };

  const handleFetchData = async () => {
    setFetchingData(true);
    try {
      const result = await onFetchData(company.id);
      setEditData(result);
      toast.success(result._note === 'Nem található nyilvános adat' ? "Nem található adat" : "Cégadatok lekérve!");
    } catch (e) {
      toast.error("Hiba az adatok lekérésekor");
    }
    setFetchingData(false);
  };

  const handleDelete = async () => {
    try {
      await onDelete(company.id);
      setShowDeleteConfirm(false);
      onOpenChange(false);
      toast.success("Partner törölve!");
    } catch (e) {
      toast.error("Hiba a törléskor");
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await onUpdate(company.id, { status: newStatus });
      setEditData(prev => ({ ...prev, status: newStatus }));
      await axios.post(`${API}/companies/${company.id}/logs`, {
        entry_type: "Státusz változás",
        content: `Státusz: ${newStatus}`
      });
      await fetchLogs();
      toast.success(`Státusz: ${newStatus}`);
    } catch (e) {
      toast.error("Hiba a státusz módosításakor");
    }
  };

  if (!company) return null;

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full sm:w-[600px] p-0 flex flex-col" data-testid="company-detail-sheet">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b z-10 p-4">
            <SheetHeader>
              <SheetTitle className="text-xl font-bold flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                {company.name}
              </SheetTitle>
            </SheetHeader>
            
            {/* Upcoming reminders badge */}
            {reminders.length > 0 && (
              <div className="mt-2 p-2 bg-orange-50 rounded-lg border border-orange-200">
                <div className="flex items-center gap-2 text-orange-700 text-sm">
                  <Bell className="w-4 h-4" />
                  <span className="font-medium">{reminders.length} aktív emlékeztető</span>
                </div>
                <p className="text-xs text-orange-600 mt-1">
                  Következő: {reminders[0].next_step} ({formatDate(reminders[0].remind_at)})
                </p>
              </div>
            )}
            
            {/* Status selector - only if user has status_edit permission */}
            {userPermissions?.status_edit && (
            <div className="mt-3">
              <Select value={editData.status || company.status} onValueChange={handleStatusChange}>
                <SelectTrigger className="w-full" data-testid="status-change-select">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: STATUS_COLORS[editData.status || company.status] }} />
                    <SelectValue />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {statuses.map(status => (
                    <SelectItem key={status} value={status}>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: STATUS_COLORS[status] }} />
                        {status}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            )}
            {!userPermissions?.status_edit && (
            <div className="mt-3 flex items-center gap-2 px-3 py-2 bg-gray-50 rounded">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: STATUS_COLORS[company.status] }} />
              <span className="text-sm">{company.status}</span>
            </div>
            )}

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="info" className="text-xs">
                  <FileText className="w-3 h-3 mr-1" /> Adatok
                </TabsTrigger>
                <TabsTrigger value="positions" className="text-xs">
                  <Briefcase className="w-3 h-3 mr-1" /> Pozíciók ({positions.length})
                </TabsTrigger>
                <TabsTrigger value="log" className="text-xs">
                  <MessageSquare className="w-3 h-3 mr-1" /> Napló ({logs.length})
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {/* Info Tab */}
            {activeTab === "info" && (
              <div className="p-4 space-y-3">
                {isEditing ? (
                  <>
                    <div>
                      <Label className="text-xs text-gray-500">Iparág</Label>
                      <Select value={editData.industry || ''} onValueChange={(val) => setEditData(prev => ({ ...prev, industry: val }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {industries.map(ind => <SelectItem key={ind} value={ind}>{ind}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs text-gray-500">Város</Label>
                        <Input value={editData.city || ''} onChange={(e) => setEditData(prev => ({ ...prev, city: e.target.value }))} />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Cím</Label>
                        <Input value={editData.address || ''} onChange={(e) => setEditData(prev => ({ ...prev, address: e.target.value }))} />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs text-gray-500">Telefon</Label>
                        <Input value={editData.phone || ''} onChange={(e) => setEditData(prev => ({ ...prev, phone: e.target.value }))} />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Email</Label>
                        <Input value={editData.email || ''} onChange={(e) => setEditData(prev => ({ ...prev, email: e.target.value }))} />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Kapcsolattartó</Label>
                      <Input value={editData.contact_person || ''} onChange={(e) => setEditData(prev => ({ ...prev, contact_person: e.target.value }))} />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Adószám</Label>
                      <Input value={editData.tax_number || ''} onChange={(e) => setEditData(prev => ({ ...prev, tax_number: e.target.value }))} />
                    </div>
                  </>
                ) : (
                  <>
                    <InfoItem icon={Factory} label="Iparág" value={editData.industry} />
                    <InfoItem icon={MapPin} label="Helyszín" value={[editData.city, editData.address].filter(Boolean).join(', ')} />
                    <InfoItem icon={User} label="Kapcsolattartó" value={editData.contact_person} />
                    <InfoItem icon={Phone} label="Telefon" value={editData.phone} />
                    <InfoItem icon={Mail} label="Email" value={editData.email} />
                    <InfoItem icon={Globe} label="Weboldal" value={editData.website} />
                    <InfoItem icon={FileText} label="Adószám" value={editData.tax_number} />
                    
                    {(editData.employee_count || editData.revenue || editData.founded_year) && (
                      <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                        <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">Cégadatok</h4>
                        {editData.employee_count && <p className="text-sm">Létszám: {editData.employee_count} fő</p>}
                        {editData.revenue && <p className="text-sm">Bevétel: {editData.revenue}</p>}
                        {editData.founded_year && <p className="text-sm">Alapítás: {editData.founded_year}</p>}
                      </div>
                    )}
                    
                    {editData.latitude && editData.longitude && (
                      <div className="text-xs text-gray-400 mt-2">
                        <MapPin className="w-3 h-3 inline mr-1" />
                        {editData.latitude.toFixed(4)}, {editData.longitude.toFixed(4)}
                      </div>
                    )}
                  </>
                )}
                
                <div className="flex gap-2 pt-4 border-t">
                  {isEditing ? (
                    <>
                      <Button onClick={handleSave} disabled={loading} className="flex-1">
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Mentés"}
                      </Button>
                      <Button variant="outline" onClick={() => { setIsEditing(false); setEditData(company); }}>Mégse</Button>
                    </>
                  ) : (
                    <>
                      {userPermissions?.status_edit && (
                        <Button variant="outline" onClick={() => setIsEditing(true)} className="flex-1" data-testid="edit-company-btn">Szerkesztés</Button>
                      )}
                      <Button variant="outline" onClick={handleFetchData} disabled={fetchingData} data-testid="fetch-data-btn">
                        {fetchingData ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                      </Button>
                      {userPermissions?.delete && (
                        <Button variant="destructive" onClick={() => setShowDeleteConfirm(true)} data-testid="delete-company-btn">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Positions Tab */}
            {activeTab === "positions" && (
              <div className="p-4">
                <div className="bg-gray-50 p-3 rounded-lg mb-4">
                  <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">Új pozíció</h4>
                  <div className="flex gap-2 mb-2">
                    <Input
                      placeholder="Pozíció neve (pl. Raktáros)"
                      value={newPosition.name}
                      onChange={(e) => setNewPosition(prev => ({ ...prev, name: e.target.value }))}
                      className="flex-1"
                    />
                    <Input
                      type="number"
                      placeholder="Fő"
                      value={newPosition.headcount}
                      onChange={(e) => setNewPosition(prev => ({ ...prev, headcount: parseInt(e.target.value) || 1 }))}
                      className="w-20"
                      min={1}
                    />
                  </div>
                  <Input
                    placeholder="Megjegyzés (opcionális)"
                    value={newPosition.notes}
                    onChange={(e) => setNewPosition(prev => ({ ...prev, notes: e.target.value }))}
                    className="mb-2"
                  />
                  <Button onClick={handleAddPosition} disabled={addingPosition} size="sm" className="w-full">
                    {addingPosition ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Hozzáadás</>}
                  </Button>
                </div>

                {positions.length === 0 ? (
                  <p className="text-center text-gray-400 py-8">Még nincs pozíció felvéve</p>
                ) : (
                  <div className="space-y-2">
                    {positions.map(pos => (
                      <div key={pos.id} className="flex items-center justify-between p-3 bg-white border rounded-lg">
                        <div>
                          <div className="flex items-center gap-2">
                            <Briefcase className="w-4 h-4 text-gray-400" />
                            <span className="font-medium">{pos.name}</span>
                            <Badge variant="secondary">{pos.headcount} fő</Badge>
                          </div>
                          {pos.notes && <p className="text-xs text-gray-500 mt-1">{pos.notes}</p>}
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => handleDeletePosition(pos.id)}>
                          <Trash2 className="w-4 h-4 text-gray-400" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Log Tab */}
            {activeTab === "log" && (
              <div className="p-4">
                {/* Active reminders */}
                {reminders.length > 0 && (
                  <div className="mb-4 p-3 bg-orange-50 rounded-lg border border-orange-200">
                    <h4 className="text-xs font-medium text-orange-700 uppercase mb-2 flex items-center gap-1">
                      <Bell className="w-3 h-3" /> Aktív emlékeztetők
                    </h4>
                    <div className="space-y-2">
                      {reminders.map(r => (
                        <div key={r.id} className="flex items-center justify-between text-sm">
                          <div>
                            <span className="font-medium">{r.next_step}</span>
                            <span className="text-orange-600 ml-2">({formatDate(r.remind_at)})</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={async () => {
                                try {
                                  await axios.post(`${API}/reminders/${r.id}/send-email`);
                                  toast.success("Email elküldve!");
                                } catch (e) {
                                  toast.error(e.response?.data?.detail || "Email küldés sikertelen");
                                }
                              }} 
                              className="text-blue-600 h-7 w-7 p-0"
                              title="Email küldése"
                            >
                              <Mail className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleMarkReminderDone(r.id)} className="text-green-600 h-7 w-7 p-0" title="Kész">
                              <Check className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Quick log buttons */}
                <div className="mb-4">
                  <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">Gyors naplózás</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {QUICK_LOG_BUTTONS.map(btn => (
                      <Button
                        key={btn.type}
                        variant="outline"
                        size="sm"
                        className={`justify-start ${btn.color}`}
                        onClick={() => handleQuickLog(btn.type)}
                      >
                        <btn.icon className="w-4 h-4 mr-2" />
                        {btn.type}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Custom log entry */}
                <div className="bg-gray-50 p-3 rounded-lg mb-4">
                  <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">Egyéni bejegyzés</h4>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Írd be a bejegyzést..."
                      value={customLogContent}
                      onChange={(e) => setCustomLogContent(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleCustomLog()}
                    />
                    <Button onClick={handleCustomLog} disabled={addingLog}>
                      {addingLog ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>

                {/* Log entries */}
                <div className="space-y-2">
                  {logs.length === 0 ? (
                    <p className="text-center text-gray-400 py-8">Még nincs napló bejegyzés</p>
                  ) : (
                    logs.map(log => (
                      <div key={log.id} className="p-3 bg-white border rounded-lg">
                        <div className="flex items-start gap-2">
                          <Clock className="w-3 h-3 text-gray-400 mt-1" />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs text-gray-500">{formatDateTime(log.created_at)}</span>
                              <Badge variant="outline" className="text-xs">{log.entry_type}</Badge>
                            </div>
                            <p className="text-sm mt-1">{log.content}</p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Reminder dialog - pops up after logging */}
      <ReminderDialog
        open={showReminderDialog}
        onOpenChange={setShowReminderDialog}
        companyId={company?.id}
        companyName={company?.name}
        logType={lastLogType}
        onCreated={() => { fetchReminders(); fetchLogs(); }}
      />

      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Biztosan törlöd?</AlertDialogTitle>
            <AlertDialogDescription>
              A "{company.name}" és minden adata véglegesen törlődik.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Mégse</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive">Törlés</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function InfoItem({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-2 border-b border-gray-100 last:border-0">
      <Icon className="w-4 h-4 text-gray-400 mt-0.5" />
      <div>
        <span className="text-xs text-gray-500">{label}</span>
        <p className="text-sm">{value}</p>
      </div>
    </div>
  );
}

function AddCompanyDialog({ open, onOpenChange, onAdd, industries }) {
  const [formData, setFormData] = useState({
    name: '', industry: '', city: '', address: '', phone: '',
    email: '', contact_person: '', notes: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error("Cégnév kötelező!");
      return;
    }
    setLoading(true);
    try {
      await onAdd(formData);
      setFormData({ name: '', industry: '', city: '', address: '', phone: '', email: '', contact_person: '', notes: '' });
      onOpenChange(false);
      toast.success("Partner hozzáadva!");
    } catch (e) {
      toast.error("Hiba történt");
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>+ Új Partner</DialogTitle>
          <DialogDescription className="sr-only">Partner hozzáadása</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <Label className="text-xs">Cégnév *</Label>
            <Input value={formData.name} onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))} required />
          </div>
          <div>
            <Label className="text-xs">Iparág</Label>
            <Select value={formData.industry} onValueChange={(val) => setFormData(prev => ({ ...prev, industry: val }))}>
              <SelectTrigger><SelectValue placeholder="Válassz..." /></SelectTrigger>
              <SelectContent>
                {industries.map(ind => <SelectItem key={ind} value={ind}>{ind}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Város</Label>
              <Input value={formData.city} onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))} />
            </div>
            <div>
              <Label className="text-xs">Kapcsolattartó</Label>
              <Input value={formData.contact_person} onChange={(e) => setFormData(prev => ({ ...prev, contact_person: e.target.value }))} />
            </div>
          </div>
          <div>
            <Label className="text-xs">Megjegyzés</Label>
            <Textarea value={formData.notes} onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))} rows={2} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Mégse</Button>
            <Button type="submit" disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Hozzáadás"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Main App
function App() {
  const [currentUser, setCurrentUser] = useState(null); // null=checking, false=not logged in
  const [authChecking, setAuthChecking] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeStatus, setActiveStatus] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Geo search
  const [searchCity, setSearchCity] = useState('');
  const [searchRadius, setSearchRadius] = useState('30');
  const [searchIndustry, setSearchIndustry] = useState('');
  const [geoSearching, setGeoSearching] = useState(false);
  const [geoFilterActive, setGeoFilterActive] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [geocodeStatus, setGeocodeStatus] = useState(null);

  // Due reminders
  const [dueReminders, setDueReminders] = useState([]);

  // Profession.hu jobs
  const [professionJobs, setProfessionJobs] = useState([]);
  const [professionUnseen, setProfessionUnseen] = useState(0);
  const [professionOpen, setProfessionOpen] = useState(false);
  const [professionScanning, setProfessionScanning] = useState(false);
  const [profCategories, setProfCategories] = useState([]);
  const [profCounties, setProfCounties] = useState([]);
  const [profCategory, setProfCategory] = useState('0');
  const [profCounty, setProfCounty] = useState('0');
  const [profKeyword, setProfKeyword] = useState('');
  const [profDismissedView, setProfDismissedView] = useState(false);
  const [profDismissedJobs, setProfDismissedJobs] = useState([]);
  
  // Bulk selection state
  const [profSelectMode, setProfSelectMode] = useState(false);
  const [profSelectedJobs, setProfSelectedJobs] = useState(new Set());
  
  // Profession settings state
  const [profSettingsOpen, setProfSettingsOpen] = useState(false);
  const [profEmailSettings, setProfEmailSettings] = useState({ enabled: false, categories: [], counties: [] });
  const [savedFilters, setSavedFilters] = useState([]);
  const [saveFilterOpen, setSaveFilterOpen] = useState(false);
  const [newFilterName, setNewFilterName] = useState('');

  // Check auth on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const res = await axios.get(`${API}/auth/me`);
        setCurrentUser(res.data);
      } catch (e) {
        setCurrentUser(false);
      }
      setAuthChecking(false);
    };
    checkAuth();
  }, []);

  const handleLogin = (user) => {
    setCurrentUser(user);
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (e) { /* ignore */ }
    setCurrentUser(false);
  };

  const userPermissions = currentUser?.role === 'admin'
    ? { status_edit: true, delete: true, send_email: true }
    : currentUser?.permissions || {};

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) setSidebarOpen(true);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Check for due reminders every 30 seconds
  useEffect(() => {
    const checkReminders = async () => {
      try {
        const res = await axios.get(`${API}/reminders/due`);
        setDueReminders(res.data);
      } catch (e) { console.error(e); }
    };
    checkReminders();
    const interval = setInterval(checkReminders, 30000);
    return () => clearInterval(interval);
  }, []);

  // Check for unseen profession.hu jobs periodically
  useEffect(() => {
    if (!currentUser) return;
    const checkProfession = async () => {
      try {
        const res = await axios.get(`${API}/profession/jobs/count-unseen`);
        setProfessionUnseen(res.data.count);
      } catch (e) { /* ignore */ }
    };
    const loadFilters = async () => {
      try {
        const [cats, counties] = await Promise.all([
          axios.get(`${API}/profession/categories`),
          axios.get(`${API}/profession/counties`),
        ]);
        setProfCategories(cats.data);
        setProfCounties(counties.data);
      } catch (e) { /* ignore */ }
    };
    checkProfession();
    loadFilters();
    const interval = setInterval(checkProfession, 60000);
    return () => clearInterval(interval);
  }, [currentUser]);

  const fetchProfessionJobs = async () => {
    try {
      const res = await axios.get(`${API}/profession/jobs`);
      setProfessionJobs(res.data);
    } catch (e) { toast.error("Hiba a profession.hu adatok betöltésekor"); }
  };

  const handleProfessionScan = async () => {
    setProfessionScanning(true);
    try {
      const params = new URLSearchParams();
      if (profCategory !== '0') params.append('category_id', profCategory);
      if (profCounty !== '0') params.append('county_id', profCounty);
      if (profKeyword) params.append('keyword', profKeyword);

      const res = await axios.post(`${API}/profession/search?${params.toString()}`);
      setProfessionJobs(res.data.jobs || []);
      const matches = res.data.crm_matches || 0;
      toast.success(`${res.data.total} állás találva${matches > 0 ? `, ebből ${matches} CRM cég!` : ''}`);
      const countRes = await axios.get(`${API}/profession/jobs/count-unseen`);
      setProfessionUnseen(countRes.data.count);
    } catch (e) {
      toast.error("Hiba a kereséskor");
    }
    setProfessionScanning(false);
  };

  const handleMarkJobSeen = async (jobId) => {
    try {
      await axios.post(`${API}/profession/jobs/${jobId}/seen`);
      setProfessionJobs(prev => prev.map(j => j.id === jobId ? {...j, seen: true, is_new: false} : j));
      setProfessionUnseen(prev => Math.max(0, prev - 1));
    } catch (e) { /* ignore */ }
  };

  const handleMarkAllSeen = async () => {
    try {
      await axios.post(`${API}/profession/jobs/mark-all-seen`);
      setProfessionJobs(prev => prev.map(j => ({...j, seen: true, is_new: false})));
      setProfessionUnseen(0);
    } catch (e) { /* ignore */ }
  };

  const handleAddJobToCrm = async (jobId) => {
    try {
      await axios.post(`${API}/profession/jobs/${jobId}/add-to-crm`);
      setProfessionJobs(prev => prev.map(j => j.id === jobId ? {...j, added_to_crm: true} : j));
      toast.success("Pozíció hozzáadva a CRM-hez!");
    } catch (e) {
      toast.error("Hiba");
    }
  };

  const handleDismissJob = async (jobId) => {
    try {
      await axios.post(`${API}/profession/jobs/${jobId}/dismiss`);
      setProfessionJobs(prev => prev.filter(j => j.id !== jobId));
      toast("Nem érdekes - elrejtve");
    } catch (e) { /* ignore */ }
  };

  const handleRestoreJob = async (jobId) => {
    try {
      await axios.post(`${API}/profession/jobs/${jobId}/restore`);
      setProfDismissedJobs(prev => prev.filter(j => j.id !== jobId));
      toast.success("Visszaállítva");
    } catch (e) { /* ignore */ }
  };

  // Bulk operations
  const handleBulkDismiss = async () => {
    if (profSelectedJobs.size === 0) return;
    try {
      const jobIds = Array.from(profSelectedJobs);
      await axios.post(`${API}/profession/jobs/bulk-dismiss`, { job_ids: jobIds });
      setProfessionJobs(prev => prev.filter(j => !profSelectedJobs.has(j.id)));
      setProfSelectedJobs(new Set());
      setProfSelectMode(false);
      toast.success(`${jobIds.length} állás elrejtve`);
    } catch (e) {
      toast.error("Hiba");
    }
  };

  const handleBulkRestore = async () => {
    if (profSelectedJobs.size === 0) return;
    try {
      const jobIds = Array.from(profSelectedJobs);
      await axios.post(`${API}/profession/jobs/bulk-restore`, { job_ids: jobIds });
      setProfDismissedJobs(prev => prev.filter(j => !profSelectedJobs.has(j.id)));
      setProfSelectedJobs(new Set());
      setProfSelectMode(false);
      toast.success(`${jobIds.length} állás visszaállítva`);
    } catch (e) {
      toast.error("Hiba");
    }
  };

  const toggleSelectJob = (jobId) => {
    setProfSelectedJobs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(jobId)) {
        newSet.delete(jobId);
      } else {
        newSet.add(jobId);
      }
      return newSet;
    });
  };

  const selectAllJobs = () => {
    const jobs = profDismissedView ? profDismissedJobs : professionJobs;
    setProfSelectedJobs(new Set(jobs.map(j => j.id)));
  };

  // User settings functions
  const loadUserSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      if (res.data.profession_email) {
        setProfEmailSettings(res.data.profession_email);
      }
      if (res.data.saved_filters) {
        setSavedFilters(res.data.saved_filters);
      }
    } catch (e) { /* ignore */ }
  };

  const handleSaveProfSettings = async () => {
    try {
      await axios.put(`${API}/settings/profession-email`, profEmailSettings);
      toast.success("Beállítások mentve!");
      setProfSettingsOpen(false);
    } catch (e) {
      toast.error("Hiba a mentéskor");
    }
  };

  const handleSaveFilter = async () => {
    if (!newFilterName.trim()) {
      toast.error("Add meg a szűrő nevét!");
      return;
    }
    try {
      const res = await axios.post(`${API}/settings/saved-filters`, {
        name: newFilterName,
        category_id: profCategory,
        county_id: profCounty,
        keyword: profKeyword
      });
      setSavedFilters(prev => [...prev, res.data.filter]);
      setNewFilterName('');
      setSaveFilterOpen(false);
      toast.success("Szűrő mentve!");
    } catch (e) {
      toast.error("Hiba");
    }
  };

  const handleDeleteFilter = async (filterId) => {
    try {
      await axios.delete(`${API}/settings/saved-filters/${filterId}`);
      setSavedFilters(prev => prev.filter(f => f.id !== filterId));
      toast.success("Szűrő törölve");
    } catch (e) {
      toast.error("Hiba");
    }
  };

  const applyFilter = (filter) => {
    setProfCategory(filter.category_id || '0');
    setProfCounty(filter.county_id || '0');
    setProfKeyword(filter.keyword || '');
    toast(`Szűrő alkalmazva: ${filter.name}`);
  };

  // Load settings when profession dialog opens
  useEffect(() => {
    if (professionOpen) {
      loadUserSettings();
    }
  }, [professionOpen]);

  const loadDismissedJobs = async () => {
    try {
      const res = await axios.get(`${API}/profession/jobs/dismissed`);
      setProfDismissedJobs(res.data);
    } catch (e) { /* ignore */ }
  };

  const handleOpenProfession = async () => {
    setProfessionOpen(true);
  };

  const fetchCompanies = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/companies`);
      setCompanies(res.data);
    } catch (e) {
      toast.error("Hiba a betöltéskor");
    }
    setLoading(false);
  }, []);

  const fetchStatuses = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/statuses`);
      setStatuses(res.data);
    } catch (e) { console.error(e); }
  }, []);

  const fetchIndustries = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/industries`);
      setIndustries(res.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    fetchCompanies();
    fetchStatuses();
    fetchIndustries();
  }, [fetchCompanies, fetchStatuses, fetchIndustries]);

  const handleAddCompany = async (data) => {
    const res = await axios.post(`${API}/companies`, data);
    setCompanies(prev => [...prev, res.data]);
  };

  const handleUpdateCompany = async (id, data) => {
    const res = await axios.put(`${API}/companies/${id}`, data);
    setCompanies(prev => prev.map(c => c.id === id ? res.data : c));
    setSelectedCompany(res.data);
    return res.data;
  };

  const handleDeleteCompany = async (id) => {
    await axios.delete(`${API}/companies/${id}`);
    setCompanies(prev => prev.filter(c => c.id !== id));
  };

  const handleFetchCompanyData = async (id) => {
    const res = await axios.get(`${API}/companies/${id}/fetch-data`);
    setCompanies(prev => prev.map(c => c.id === id ? res.data : c));
    return res.data;
  };

  const handleGeocodeAll = async () => {
    setGeocoding(true);
    try {
      const res = await axios.post(`${API}/companies/geocode-all?batch_size=50`);
      setGeocodeStatus(res.data);
      if (res.data.remaining > 0) {
        toast.info(`${res.data.processed} geocodolva. Még ${res.data.remaining} hátra!`);
      } else {
        toast.success("Minden geocodolva!");
        setGeocodeStatus(null);
      }
      await fetchCompanies();
    } catch (e) {
      toast.error("Hiba");
    }
    setGeocoding(false);
  };

  const handleGeoSearch = async () => {
    if (!searchCity) {
      toast.error("Add meg a várost!");
      return;
    }
    setGeoSearching(true);
    try {
      const params = { city: searchCity, radius: parseFloat(searchRadius) };
      if (searchIndustry) params.industry = searchIndustry;
      const res = await axios.get(`${API}/companies/search/radius`, { params });
      setCompanies(res.data);
      setGeoFilterActive(true);
      toast.success(`${res.data.length} találat`);
    } catch (e) {
      toast.error("Hiba a kereséskor");
    }
    setGeoSearching(false);
  };

  const handleClearGeoFilter = async () => {
    setGeoFilterActive(false);
    setSearchCity('');
    setSearchIndustry('');
    await fetchCompanies();
  };

  const handleMarkReminderDone = async (reminderId) => {
    try {
      await axios.patch(`${API}/reminders/${reminderId}/done`);
      setDueReminders(prev => prev.filter(r => r.id !== reminderId));
      toast.success("Emlékeztető kész!");
    } catch (e) {
      toast.error("Hiba");
    }
  };

  const handleSendReminderEmail = async (reminderId) => {
    try {
      await axios.post(`${API}/reminders/${reminderId}/send-email`);
      toast.success("Emlékeztető email elküldve!");
    } catch (e) {
      if (e.response?.data?.detail) {
        toast.error(e.response.data.detail);
      } else {
        toast.error("Email küldés sikertelen");
      }
    }
  };

  const handleOpenCompanyFromReminder = async (companyId) => {
    const company = companies.find(c => c.id === companyId);
    if (company) {
      setSelectedCompany(company);
      setSheetOpen(true);
    } else {
      // Fetch the company if not in current list
      try {
        const res = await axios.get(`${API}/companies/${companyId}`);
        setSelectedCompany(res.data);
        setSheetOpen(true);
      } catch (e) {
        toast.error("Cég nem található");
      }
    }
  };

  const getStatusCount = (status) => companies.filter(c => c.status === status).length;

  const filteredCompanies = companies.filter(c => {
    const matchesStatus = !activeStatus || c.status === activeStatus;
    const matchesSearch = !searchQuery || 
      c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.city?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.contact_person?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.industry?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  if (authChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!currentUser) {
    return <LoginScreen onLogin={handleLogin} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <Toaster position="top-right" richColors />
      
      {/* Due reminders notification panel */}
      <DueRemindersPanel 
        reminders={dueReminders} 
        onMarkDone={handleMarkReminderDone}
        onOpenCompany={handleOpenCompanyFromReminder}
        onSendEmail={handleSendReminderEmail}
      />
      
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-0'} fixed lg:relative inset-y-0 left-0 z-40 bg-gray-900 text-white flex-shrink-0 transition-all duration-300 overflow-hidden flex flex-col`}>
        <div className="p-4 border-b border-gray-700">
          <h1 className="text-xl font-bold">Optimal CRM</h1>
        </div>
        
        <div className="flex-1 overflow-y-auto">
          <div className="p-4">
            <h2 className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3">Státuszok</h2>
            
            <button
              onClick={() => setActiveStatus(null)}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg mb-1 transition-colors ${!activeStatus ? 'bg-gray-700' : 'hover:bg-gray-800'}`}
            >
              <span className="text-sm">Összes</span>
              <span className="text-xs text-gray-400">{companies.length}</span>
            </button>
            
            {statuses.map(status => (
              <button
                key={status}
                onClick={() => setActiveStatus(status)}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg mb-1 transition-colors ${activeStatus === status ? 'bg-gray-700' : 'hover:bg-gray-800'}`}
              >
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: STATUS_COLORS[status] }} />
                  <span className="text-sm truncate">{status}</span>
                </div>
                <span className="text-xs text-gray-400">{getStatusCount(status)}</span>
              </button>
            ))}
          </div>

          {/* Geo search */}
          <div className="p-4 border-t border-gray-700">
            <h2 className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3">Körzetkeresés</h2>
            <Input value={searchCity} onChange={(e) => setSearchCity(e.target.value)} placeholder="Város" className="bg-gray-800 border-gray-600 text-white placeholder:text-gray-500 mb-2" />
            <Select value={searchRadius} onValueChange={setSearchRadius}>
              <SelectTrigger className="bg-gray-800 border-gray-600 text-white mb-2"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10 km</SelectItem>
                <SelectItem value="30">30 km</SelectItem>
                <SelectItem value="50">50 km</SelectItem>
                <SelectItem value="100">100 km</SelectItem>
              </SelectContent>
            </Select>
            <Input value={searchIndustry} onChange={(e) => setSearchIndustry(e.target.value)} placeholder="Iparág/Pozíció" className="bg-gray-800 border-gray-600 text-white placeholder:text-gray-500 mb-2" />
            <Button onClick={handleGeoSearch} disabled={geoSearching} className="w-full mb-2" size="sm">
              {geoSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : "Keresés"}
            </Button>
            {geoFilterActive && (
              <Button variant="outline" onClick={handleClearGeoFilter} className="w-full text-gray-300 border-gray-600" size="sm">
                <X className="w-4 h-4 mr-1" /> Törlés
              </Button>
            )}
            <Button variant="ghost" onClick={handleGeocodeAll} disabled={geocoding} className="w-full mt-2 text-gray-400 hover:text-white" size="sm">
              {geocoding ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
              Geocodolás
            </Button>
            {geocodeStatus?.remaining > 0 && (
              <p className="text-xs text-yellow-400 mt-1 text-center">Még {geocodeStatus.remaining} vár</p>
            )}
          </div>
        </div>

        <div className="p-4 border-t border-gray-700">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-bold">
              {currentUser?.name?.charAt(0) || '?'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{currentUser?.name}</p>
              <p className="text-xs text-gray-400">{currentUser?.role === 'admin' ? 'Admin' : 'Felhasználó'}</p>
            </div>
            <button onClick={handleLogout} className="p-1.5 hover:bg-gray-700 rounded text-gray-400 hover:text-white" data-testid="logout-btn">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
          <p className="text-[10px] text-gray-500 uppercase">Optimal CRM</p>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="bg-white border-b px-4 py-3 flex items-center gap-4">
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="lg:hidden p-2 hover:bg-gray-100 rounded">
            <Menu className="w-5 h-5" />
          </button>
          
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: activeStatus ? STATUS_COLORS[activeStatus] : '#6b7280' }} />
            <h2 className="text-lg font-semibold capitalize">{activeStatus || 'Összes'}</h2>
            <span className="text-gray-400">{filteredCompanies.length}</span>
          </div>

          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Keresés..." className="pl-10" />
            </div>
          </div>

          {/* Due reminders indicator in header */}
          {dueReminders.length > 0 && (
            <div className="flex items-center gap-1 text-orange-600 bg-orange-50 px-3 py-1 rounded-full">
              <Bell className="w-4 h-4" />
              <span className="text-sm font-medium">{dueReminders.length}</span>
            </div>
          )}

          {/* Profession.hu job alerts */}
          <button
            onClick={handleOpenProfession}
            className="relative flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            data-testid="profession-btn"
          >
            <Briefcase className="w-4 h-4 text-gray-600" />
            <span className="text-sm text-gray-600 hidden sm:inline">Állások</span>
            {professionUnseen > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                {professionUnseen > 99 ? '99+' : professionUnseen}
              </span>
            )}
          </button>

          <Button onClick={() => setAddDialogOpen(true)}>
            <Plus className="w-4 h-4 mr-2" /> Új Partner
          </Button>
        </header>

        {/* Table */}
        <div className="flex-1 overflow-auto">
          <table className="w-full">
            <thead className="bg-gray-50 sticky top-0">
              <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <th className="px-4 py-3">Cég</th>
                <th className="px-4 py-3">Iparág</th>
                <th className="px-4 py-3">Helyszín</th>
                <th className="px-4 py-3 hidden md:table-cell">Kapcsolattartó</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {filteredCompanies.map((company) => (
                <tr 
                  key={company.id}
                  onClick={() => { setSelectedCompany(company); setSheetOpen(true); }}
                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3 font-medium text-gray-900">{company.name}</td>
                  <td className="px-4 py-3 text-gray-600 text-sm">{company.industry || '-'}</td>
                  <td className="px-4 py-3 text-gray-600 text-sm">{company.city || '-'}</td>
                  <td className="px-4 py-3 text-gray-600 text-sm hidden md:table-cell">{company.contact_person || '-'}</td>
                </tr>
              ))}
              {filteredCompanies.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-12 text-center text-gray-500">
                    <Building2 className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>Nincs találat</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </main>

      <CompanyDetailSheet
        company={selectedCompany}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        onUpdate={handleUpdateCompany}
        onDelete={handleDeleteCompany}
        onFetchData={handleFetchCompanyData}
        industries={industries}
        statuses={statuses}
        onRefresh={fetchCompanies}
        userPermissions={userPermissions}
      />

      <AddCompanyDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        onAdd={handleAddCompany}
        industries={industries}
      />

      {/* Profession.hu Job Alerts Dialog */}
      <Dialog open={professionOpen} onOpenChange={(open) => { setProfessionOpen(open); if (!open) { setProfSelectMode(false); setProfSelectedJobs(new Set()); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col" data-testid="profession-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Briefcase className="w-5 h-5" />
              Profession.hu Állásfigyelő
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setProfSettingsOpen(true)}
                className="ml-auto h-8 w-8 p-0"
                title="Email beállítások"
              >
                <Settings className="w-4 h-4" />
              </Button>
            </DialogTitle>
            <DialogDescription>
              A CRM-ben lévő cégek állásajánlatai a profession.hu-ról
            </DialogDescription>
          </DialogHeader>

          {/* Saved filters */}
          {savedFilters.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2">
              {savedFilters.map(filter => (
                <div key={filter.id} className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => applyFilter(filter)}
                    className="h-6 text-xs px-2"
                  >
                    <Star className="w-3 h-3 mr-1 text-yellow-500" />
                    {filter.name}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteFilter(filter.id)}
                    className="h-6 w-6 p-0 text-gray-400 hover:text-red-500"
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          <div className="flex flex-wrap items-end gap-2 mb-3">
            <div className="flex-1 min-w-[140px]">
              <Label className="text-xs text-gray-500">Kategória</Label>
              <Select value={profCategory} onValueChange={setProfCategory}>
                <SelectTrigger className="h-8 text-xs" data-testid="prof-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {profCategories.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[140px]">
              <Label className="text-xs text-gray-500">Megye</Label>
              <Select value={profCounty} onValueChange={setProfCounty}>
                <SelectTrigger className="h-8 text-xs" data-testid="prof-county-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {profCounties.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[120px]">
              <Label className="text-xs text-gray-500">Kulcsszó</Label>
              <Input
                value={profKeyword}
                onChange={e => setProfKeyword(e.target.value)}
                placeholder="pl. CNC, villanyszerelő"
                className="h-8 text-xs"
                data-testid="prof-keyword-input"
                onKeyDown={e => e.key === 'Enter' && handleProfessionScan()}
              />
            </div>
            <Button onClick={handleProfessionScan} disabled={professionScanning} size="sm" className="h-8" data-testid="profession-scan-btn">
              {professionScanning ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Search className="w-3 h-3 mr-1" />}
              Keresés
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSaveFilterOpen(true)}
              className="h-8"
              title="Szűrő mentése"
            >
              <Save className="w-3 h-3" />
            </Button>
          </div>

          <div className="flex items-center gap-2 mb-3">
            <button
              onClick={() => { setProfDismissedView(false); setProfSelectMode(false); setProfSelectedJobs(new Set()); }}
              className={`text-sm px-3 py-1 rounded-full transition-colors ${!profDismissedView ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              data-testid="prof-tab-results"
            >
              Találatok {professionJobs.length > 0 && `(${professionJobs.length})`}
            </button>
            <button
              onClick={() => { setProfDismissedView(true); loadDismissedJobs(); setProfSelectMode(false); setProfSelectedJobs(new Set()); }}
              className={`text-sm px-3 py-1 rounded-full transition-colors ${profDismissedView ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              data-testid="prof-tab-dismissed"
            >
              Nem érdekes {profDismissedJobs.length > 0 && `(${profDismissedJobs.length})`}
            </button>
            
            <div className="flex-1" />
            
            {/* Bulk selection toggle */}
            {((profDismissedView && profDismissedJobs.length > 0) || (!profDismissedView && professionJobs.length > 0)) && (
              <Button
                variant={profSelectMode ? "default" : "outline"}
                size="sm"
                onClick={() => { setProfSelectMode(!profSelectMode); setProfSelectedJobs(new Set()); }}
                className="h-7 text-xs"
                data-testid="bulk-select-btn"
              >
                <CheckSquare className="w-3 h-3 mr-1" />
                {profSelectMode ? 'Kész' : 'Kijelölés'}
              </Button>
            )}
            
            {!profDismissedView && professionUnseen > 0 && (
              <Button variant="outline" size="sm" onClick={handleMarkAllSeen} className="h-7 text-xs" data-testid="profession-mark-all-seen">
                <Check className="w-3 h-3 mr-1" /> Mind olvasott
              </Button>
            )}
            <span className="text-[10px] text-gray-400">
              Auto: naponta 16:00
            </span>
          </div>

          {/* Bulk actions bar */}
          {profSelectMode && profSelectedJobs.size > 0 && (
            <div className="flex items-center gap-2 p-2 mb-2 bg-blue-50 rounded-lg border border-blue-200">
              <span className="text-sm font-medium text-blue-700">{profSelectedJobs.size} kiválasztva</span>
              <Button variant="outline" size="sm" onClick={selectAllJobs} className="h-7 text-xs ml-auto">
                Összes
              </Button>
              <Button variant="outline" size="sm" onClick={() => setProfSelectedJobs(new Set())} className="h-7 text-xs">
                Törlés
              </Button>
              {profDismissedView ? (
                <Button size="sm" onClick={handleBulkRestore} className="h-7 text-xs bg-green-600 hover:bg-green-700" data-testid="bulk-restore-btn">
                  <Undo2 className="w-3 h-3 mr-1" /> Visszaállítás
                </Button>
              ) : (
                <Button size="sm" onClick={handleBulkDismiss} className="h-7 text-xs bg-red-600 hover:bg-red-700" data-testid="bulk-dismiss-btn">
                  <EyeOff className="w-3 h-3 mr-1" /> Nem érdekes
                </Button>
              )}
            </div>
          )}

          <ScrollArea className="flex-1 -mx-6 px-6">
            {profDismissedView ? (
              /* Nem érdekes lista */
              profDismissedJobs.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <EyeOff className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Nincs elrejtett állás</p>
                  <p className="text-sm mt-1">A "Nem érdekes"-nek jelölt állások itt jelennek meg</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {profDismissedJobs.map(job => (
                    <div key={job.id} className="relative p-3 rounded-lg border border-gray-200 bg-gray-50" data-testid="dismissed-job-card">
                      {/* Checkbox for bulk selection */}
                      {profSelectMode && (
                        <Checkbox
                          checked={profSelectedJobs.has(job.id)}
                          onCheckedChange={() => toggleSelectJob(job.id)}
                          className="absolute top-3 left-3"
                        />
                      )}
                      
                      {/* 3 pontos menü a jobb felső sarokban */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="absolute top-2 right-2 h-7 w-7 p-0 hover:bg-gray-200"
                            data-testid="dismissed-job-menu-btn"
                          >
                            <MoreVertical className="w-4 h-4 text-gray-400" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem
                            onClick={() => handleRestoreJob(job.id)}
                            className="cursor-pointer text-green-600 focus:text-green-600"
                            data-testid="restore-job-menu-btn"
                          >
                            <Undo2 className="w-4 h-4 mr-2" />
                            Visszaállítás
                          </DropdownMenuItem>
                          {job.link && (
                            <DropdownMenuItem
                              onClick={() => window.open(job.link, '_blank')}
                              className="cursor-pointer"
                            >
                              <Globe className="w-4 h-4 mr-2" />
                              Állás megtekintése
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>

                      <div className={`flex items-start justify-between gap-2 ${profSelectMode ? 'pl-8' : ''} pr-8`}>
                        <div className="flex-1 min-w-0">
                          <span className="text-xs text-gray-500 font-medium">{job.crm_company_name || job.company}</span>
                          <p className="text-sm text-gray-700 mt-0.5">{job.title}</p>
                          <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                            {job.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{job.location}</span>}
                            {job.date_posted && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{job.date_posted}</span>}
                          </div>
                        </div>
                        {!profSelectMode && (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => handleRestoreJob(job.id)} 
                            className="text-xs h-7 px-2 text-green-600 border-green-300 hover:bg-green-50 hover:text-green-700" 
                            data-testid="restore-job-btn"
                          >
                            <Undo2 className="w-3 h-3 mr-1" /> Visszaállít
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : professionJobs.length === 0 ? (
              /* Nincs találat */
              <div className="text-center py-12 text-gray-500">
                <Briefcase className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Nincs találat</p>
                <p className="text-sm mt-1">Nyomj a "Keresés" gombra</p>
              </div>
            ) : (
              <div className="space-y-2">
                {professionJobs.map(job => (
                  <div
                    key={job.id}
                    className={`relative p-3 rounded-lg border transition-colors ${job.is_crm_match ? 'bg-green-50 border-green-200' : job.is_new ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200'}`}
                    data-testid="profession-job-card"
                  >
                    {/* Checkbox for bulk selection */}
                    {profSelectMode && (
                      <Checkbox
                        checked={profSelectedJobs.has(job.id)}
                        onCheckedChange={() => toggleSelectJob(job.id)}
                        className="absolute top-3 left-3"
                      />
                    )}
                    
                    {/* 3 pontos menü a jobb felső sarokban */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="absolute top-2 right-2 h-7 w-7 p-0 hover:bg-gray-200"
                          data-testid="job-menu-btn"
                        >
                          <MoreVertical className="w-4 h-4 text-gray-500" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        {job.crm_company_id && (
                          <DropdownMenuItem
                            onClick={() => {
                              const comp = companies.find(c => c.id === job.crm_company_id);
                              if (comp) {
                                setProfessionOpen(false);
                                setSelectedCompany(comp);
                                setSheetOpen(true);
                              }
                            }}
                            className="cursor-pointer"
                          >
                            <Building2 className="w-4 h-4 mr-2" />
                            CRM Adatlap
                          </DropdownMenuItem>
                        )}
                        {!job.added_to_crm && job.crm_company_id && (
                          <DropdownMenuItem
                            onClick={() => handleAddJobToCrm(job.id)}
                            className="cursor-pointer"
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Pozíció hozzáadása
                          </DropdownMenuItem>
                        )}
                        {!job.seen && (
                          <DropdownMenuItem
                            onClick={() => handleMarkJobSeen(job.id)}
                            className="cursor-pointer"
                          >
                            <Check className="w-4 h-4 mr-2" />
                            Olvasottnak jelölés
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => handleDismissJob(job.id)}
                          className="cursor-pointer text-red-600 focus:text-red-600"
                          data-testid="dismiss-job-btn"
                        >
                          <EyeOff className="w-4 h-4 mr-2" />
                          Nem érdekes
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>

                    <div className={`flex items-start justify-between gap-2 ${profSelectMode ? 'pl-8' : ''} pr-8`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          {job.is_new && <Badge variant="default" className="text-[10px] px-1.5 py-0">ÚJ</Badge>}
                          {job.is_crm_match && (
                            <Badge className="text-[10px] px-1.5 py-0 bg-green-600">CRM</Badge>
                          )}
                          {job.crm_status && (
                            <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border" style={{ borderColor: STATUS_COLORS[job.crm_status] || '#999', color: STATUS_COLORS[job.crm_status] || '#999' }}>
                              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: STATUS_COLORS[job.crm_status] || '#999' }} />
                              {job.crm_status}
                            </span>
                          )}
                          <span className="text-xs text-gray-500 font-medium truncate">{job.crm_company_name || job.company}</span>
                        </div>
                        <a
                          href={job.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-sm text-gray-900 hover:text-blue-600 hover:underline"
                        >
                          {job.title}
                        </a>
                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                          {job.location && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />{job.location}
                            </span>
                          )}
                          {job.date_posted && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />{job.date_posted}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {job.added_to_crm && (
                          <Badge variant="secondary" className="text-[10px]">CRM-ben</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Profession Settings Dialog */}
      <Dialog open={profSettingsOpen} onOpenChange={setProfSettingsOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Email Értesítés Beállítások
            </DialogTitle>
            <DialogDescription>
              Napi összefoglaló emailek beállítása
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="flex-1 -mx-6 px-6">
            <div className="space-y-6 py-4">
              {/* Email értesítés kapcsoló */}
              <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="font-medium">Napi email összefoglaló</p>
                    <p className="text-sm text-gray-500">Naponta 16:30-kor küldjük a kiválasztott szűrők alapján</p>
                  </div>
                </div>
                <Switch
                  checked={profEmailSettings.enabled}
                  onCheckedChange={(checked) => setProfEmailSettings(prev => ({ ...prev, enabled: checked }))}
                />
              </div>

              {/* Kategóriák */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-medium">Kategóriák (ha üres, akkor mind)</Label>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setProfEmailSettings(prev => ({ ...prev, categories: profCategories.filter(c => c.id !== "0").map(c => c.id) }))} className="h-7 text-xs">
                      Összes
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setProfEmailSettings(prev => ({ ...prev, categories: [] }))} className="h-7 text-xs">
                      Törlés
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 border rounded-lg bg-gray-50">
                  {profCategories.filter(c => c.id !== "0").map(cat => (
                    <label
                      key={cat.id}
                      className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                        profEmailSettings.categories.includes(cat.id) ? 'bg-blue-100 border-blue-300' : 'bg-white border-gray-200'
                      } border`}
                    >
                      <Checkbox
                        checked={profEmailSettings.categories.includes(cat.id)}
                        onCheckedChange={() => {
                          setProfEmailSettings(prev => ({
                            ...prev,
                            categories: prev.categories.includes(cat.id)
                              ? prev.categories.filter(c => c !== cat.id)
                              : [...prev.categories, cat.id]
                          }));
                        }}
                      />
                      <span className="text-sm truncate">{cat.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Megyék */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-medium">Megyék (ha üres, akkor mind)</Label>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setProfEmailSettings(prev => ({ ...prev, counties: profCounties.filter(c => c.id !== "0").map(c => c.id) }))} className="h-7 text-xs">
                      Összes
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setProfEmailSettings(prev => ({ ...prev, counties: [] }))} className="h-7 text-xs">
                      Törlés
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 border rounded-lg bg-gray-50">
                  {profCounties.filter(c => c.id !== "0").map(county => (
                    <label
                      key={county.id}
                      className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                        profEmailSettings.counties.includes(county.id) ? 'bg-green-100 border-green-300' : 'bg-white border-gray-200'
                      } border`}
                    >
                      <Checkbox
                        checked={profEmailSettings.counties.includes(county.id)}
                        onCheckedChange={() => {
                          setProfEmailSettings(prev => ({
                            ...prev,
                            counties: prev.counties.includes(county.id)
                              ? prev.counties.filter(c => c !== county.id)
                              : [...prev.counties, county.id]
                          }));
                        }}
                      />
                      <span className="text-sm truncate">{county.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </ScrollArea>

          <DialogFooter>
            <Button variant="outline" onClick={() => setProfSettingsOpen(false)}>Mégse</Button>
            <Button onClick={handleSaveProfSettings}>
              <Save className="w-4 h-4 mr-2" /> Mentés
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save Filter Dialog */}
      <Dialog open={saveFilterOpen} onOpenChange={setSaveFilterOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Szűrő mentése</DialogTitle>
            <DialogDescription>Add meg a szűrő nevét a gyors eléréshez</DialogDescription>
          </DialogHeader>
          <Input
            value={newFilterName}
            onChange={(e) => setNewFilterName(e.target.value)}
            placeholder="Szűrő neve (pl. Budapest - Gyártás)"
            onKeyDown={(e) => e.key === 'Enter' && handleSaveFilter()}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveFilterOpen(false)}>Mégse</Button>
            <Button onClick={handleSaveFilter}>
              <Star className="w-4 h-4 mr-2" /> Mentés
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default App;
